import { BookOpen, Award, Quote, GraduationCap, Globe } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CONTENT = {
  en: {
    seriesLabel: "BRAIN DRAIN SERIES",
    aboutTitle: "About This Series",
    aboutP1: "The Brain Drain Series is a structured collection of field research notes, lessons learned, and professional observations accumulated by Charles Muchina across more than a decade of engineering and quality practice \u2014 spanning Manufacturing Quality, Quality Systems, Quality Assurance, Regulatory Affairs, Supplier Quality Development, Engineering Supervision, and Lead Auditor roles in both front-room and back-room audit functions.",
    aboutP2: "The source material is exactly what it sounds like: handwritten notes from supplier visits, audio recordings from post-audit reviews, annotated standards, and the kind of hard-won knowledge that only accumulates when you have spent enough time on the factory floor, in the audit room, and in the regulatory submission process to know what actually matters.",
    authorQuote: "This series is not what I studied. It is what I learned.",
    authorName: "Charles Muchina",
    disclaimer: "This series is for educational and professional development use only. It does not constitute formal regulatory guidance or engineering advice. Always verify standards against current editions.",
    dedicatedTo: "DEDICATED TO",
    profName: "Professor C.T. Lin",
    institution1: "College of Engineering and Computer Science",
    institution2: "California State University, Northridge",
    excellenceQuote: "Excellence is not a destination, but the natural result of a life committed to never-ending learning.",
    epigraphL1: "For the engineers who asked 'why'",
    epigraphL2: "when everyone else accepted 'how.'",
    epigraphL3: "And for the next generation who will do both.",
    ackP1: "I would like to extend a special note of gratitude to Professor C.T. Lin of the College of Engineering and Computer Science at California State University, Northridge (CSUN).",
    ackP2: "Professor Lin was more than an educator; he was a catalyst for my vision of the engineering profession. He taught me that the true difference in academic learning lies not in what you know, but in how you choose to use it. He believed that to achieve excellence, one must never stop being a student \u2014 that if you commit to constant learning, excellence eventually arrives as a natural byproduct.",
    ackP3: "This book series, BRAIN DRAIN, is my direct response to that philosophy. It represents my way of taking technical knowledge and 'draining' it into practical, real-world solutions for complex problems, exactly as Professor Lin encouraged. His wisdom remains the foundation upon which these volumes are built.",
    ackClosing: "To Professor Lin, and to every engineer who has ever taken a difficult lesson and passed it forward: this series is for you.",
    fda: "FDA 21 CFR Part 820 / QMSR (eff. 2/2/2026)",
    langSwitch: "Versão em Português",
    langUrl: "/about/pt",
    readerHubBtn: "Activate Your Free Access",
  },
  pt: {
    seriesLabel: "BRAIN DRAIN SERIES",
    aboutTitle: "Sobre Esta Serie",
    aboutP1: "A Brain Drain Series e uma coleção estruturada de notas de pesquisa de campo, lições aprendidas e observações profissionais acumuladas por Charles Muchina ao longo de mais de uma decada de pratica de engenharia e qualidade \u2014 abrangendo Qualidade de Manufatura, Sistemas de Qualidade, Garantia de Qualidade, Assuntos Regulatorios, Desenvolvimento de Qualidade de Fornecedores, Supervisão de Engenharia e funções de Auditor Lider em funções de auditoria de front-room e back-room.",
    aboutP2: "O material fonte é exatamente o que parece: notas manuscritas de visitas a fornecedores, gravações de audio de revisões pós-auditoria, normas anotadas e o tipo de conhecimento conquistado com dificuldade que so se acumula quando você passou tempo suficiente no chao de fabrica, na sala de auditoria e no processo de submissao regulatória para saber o que realmente importa.",
    authorQuote: "This series is not what I studied. It is what I learned.",
    authorName: "Charles Muchina",
    disclaimer: "Esta serie destina-se exclusivamente ao uso educacional e de desenvolvimento profissional. Não constitui orientação regulatória formal ou aconselhamento de engenharia. Sempre verifique as normas em relação as edicoes atuais.",
    dedicatedTo: "DEDICADO A",
    profName: "Professor C.T. Lin",
    institution1: "College of Engineering and Computer Science",
    institution2: "California State University, Northridge",
    excellenceQuote: "A excelencia não e um destino, mas o resultado natural de uma vida dedicada ao aprendizado continuo.",
    epigraphL1: "Para os engenheiros que perguntaram 'por que'",
    epigraphL2: "quando todos os outros aceitaram o 'como.'",
    epigraphL3: "E para a proxima geração que fara as duas coisas.",
    ackP1: "Gostaria de estender uma nota especial de gratidao ao Professor C.T. Lin, do College of Engineering and Computer Science da California State University, Northridge (CSUN).",
    ackP2: "O Professor Lin foi mais do que um educador; ele foi o catalisador da minha visao sobre a profissao de engenheiro. Ele me ensinou que a verdadeira diferenca no aprendizado academico não esta no que você sabe, mas em como você escolhe usar esse conhecimento. Ele acreditava que, para alcancar a excelencia, nunca se deve deixar de ser um estudante \u2014 e que, se você se comprometer com o aprendizado constante, a excelencia eventualmente chega como um subproduto natural.",
    ackP3: "Esta serie de livros, BRAIN DRAIN, e minha resposta direta a essa filosofia. Ela representa minha maneira de pegar o conhecimento técnico e 'escoa-lo' em solucoes praticas para problemas do mundo real, exatamente como o Professor Lin incentivou. Sua sabedoria permanece como o alicerce sobre o qual estes volumes foram construidos.",
    ackClosing: "Ao Professor Lin, e a todo engenheiro que ja recebeu uma licao dificil e a transmitiu adiante: esta serie e para você.",
    fda: "FDA 21 CFR Parte 820 / QMSR (vig. 2/2/2026)",
    langSwitch: "English Version",
    langUrl: "/about",
    readerHubBtn: "Ativar Seu Acesso Gratuito",
  },
};

const AboutSeriesPage = () => {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const location = useLocation();
  const locale = location.pathname.includes("/pt") ? "pt" : "en";
  const content = CONTENT[locale];

  return (
    <div className="min-h-screen bg-[#0F2347] text-white" data-testid="about-series-page">
      {/* Nav */}
      <nav className="border-b border-[#1B3A6B] px-6 py-4 flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#FCD34D]" />
          <span className="text-xs text-[#9CA3AF] tracking-[0.1em] font-bold">{content.seriesLabel}</span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(t.langUrl)} className="flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-white" data-testid="about-lang-switch">
            <Globe className="w-3.5 h-3.5" /> {content.langSwitch}
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12 space-y-16">
        {/* About This Series (Inside Cover Panel) */}
        <section className="bg-[#0F2347] border-2 border-[#1B3A6B] rounded-lg p-8" data-testid="about-panel">
          <h2 className="text-2xl font-bold text-white mb-6">{content.aboutTitle}</h2>
          <div className="border-t border-[#1B3A6B] pt-6 space-y-4">
            <p className="text-base text-[#D1D5DB] leading-relaxed">{content.aboutP1}</p>
            <p className="text-base text-[#D1D5DB] leading-relaxed">{content.aboutP2}</p>
          </div>
          <div className="mt-8 pt-6 border-t border-[#1B3A6B]">
            <p className="text-xl font-bold italic text-[#FCD34D] leading-relaxed">"{content.authorQuote}"</p>
            <p className="text-base italic text-[#9CA3AF] mt-2">— {content.authorName}</p>
          </div>
          <p className="text-sm text-[#6B7280] mt-6">{content.disclaimer}</p>
        </section>

        {/* Dedication */}
        <section className="text-center py-12" data-testid="dedication">
          <p className="text-sm text-[#374151] tracking-[0.2em] uppercase mb-8">{content.dedicatedTo}</p>
          <h2 className="text-3xl font-bold text-[#1B3A6B] mb-3">{content.profName}</h2>
          <p className="text-lg text-[#374151] mb-1">{content.institution1}</p>
          <p className="text-xl font-bold text-[#2563EB] mb-8">{content.institution2}</p>
          <div className="border-t border-[#1B3A6B] w-1/2 mx-auto my-8" />
          <div className="max-w-lg mx-auto">
            <span className="text-4xl text-[#D1D5DB] font-serif">"</span>
            <p className="text-xl font-bold italic text-[#1B3A6B] leading-relaxed -mt-6">{content.excellenceQuote}</p>
            <span className="text-4xl text-[#D1D5DB] font-serif float-right -mt-4">"</span>
            <div className="clear-both" />
            <p className="text-base italic text-[#9CA3AF] mt-4">— {content.profName}</p>
          </div>
        </section>

        {/* Epigraph */}
        <section className="text-center py-8" data-testid="epigraph">
          <p className="text-lg text-[#D1D5DB] mb-4">— — —</p>
          <p className="text-xl italic text-[#1B3A6B] leading-relaxed">{content.epigraphL1}</p>
          <p className="text-xl italic text-[#1B3A6B] leading-relaxed">{content.epigraphL2}</p>
          <p className="text-lg italic text-[#9CA3AF] mt-2">{content.epigraphL3}</p>
          <p className="text-lg text-[#D1D5DB] mt-4">— — —</p>
        </section>

        {/* Acknowledgments */}
        <section data-testid="acknowledgments">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#FCD34D]" /> Acknowledgments
          </h2>
          <div className="space-y-4 text-base text-[#D1D5DB] leading-relaxed">
            <p>{content.ackP1}</p>
            <p>{content.ackP2}</p>
            <p>{content.ackP3}</p>
          </div>

          {/* Pull quote */}
          <div className="bg-[#F0F4F8]/5 border-t border-b border-[#D1D5DB]/20 my-8 py-6 px-6">
            <Quote className="w-6 h-6 text-[#9CA3AF] mb-2" />
            <p className="text-lg italic text-[#D1D5DB] leading-relaxed">"{content.excellenceQuote}"</p>
            <p className="text-sm text-[#9CA3AF] mt-2">— {content.profName}, CSUN</p>
          </div>

          {/* Closing */}
          <div className="text-right mt-8 italic text-[#9CA3AF]">
            <p className="text-base leading-relaxed">{content.ackClosing}</p>
            <p className="text-base font-bold text-white mt-4">{content.authorName}</p>
            <p className="text-sm">CHARSTAN Intelligence Platform</p>
          </div>
        </section>

        {/* Standards reference */}
        <section className="bg-[#132340] border border-[#1E3A5F] rounded-lg p-6 text-center" data-testid="standards-ref">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Standards Reference</p>
          <p className="text-sm text-[#00C8E0] font-mono">{content.fda}</p>
        </section>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate(locale === "pt" ? "/leitores" : "/readers")}
            className="inline-flex items-center gap-2 bg-[#1A6BCC] hover:bg-[#1A6BCC]/80 text-white px-8 py-3 rounded font-bold text-sm transition-colors"
            data-testid="reader-hub-cta"
          >
            <GraduationCap className="w-4 h-4" /> {content.readerHubBtn}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 border-t border-[#1B3A6B]">
          <p className="text-xs text-[#6B7280]">Brain Drain Series · {content.authorName} · CHARSTAN Intelligence Platform · charstan.com</p>
        </div>
      </div>
    </div>
  );
};

export default AboutSeriesPage;
